/**
 * CodeCell — a business code in a table, marked when we made it up.
 *
 * WHY THIS IS NOT A CHIP
 * ----------------------
 * A chip per row is the obvious answer and it is wrong at list scale. Forty
 * departments, most of them auto-coded, becomes forty outlined boxes competing
 * with the data — and svdy-ui §2 is explicit that decoration earns its place by
 * communicating, not by filling space.
 *
 * The pattern used instead is this product's OWN, already shipped in
 * `ActiveWorkers`'s required-documents list: the value renders normally, and a
 * muted `· marker` follows it on the same baseline (`text.disabled`, `0.7rem`,
 * `ml: 0.75`). One alignment axis, no extra element, and it reads as a note about
 * the value rather than a badge attached to the row.
 *
 * WHY THE CODE ITSELF IS NOT MUTED
 * --------------------------------
 * Rendering an auto code in `text.disabled` was the first idea and it says the
 * wrong thing: in this codebase that colour means absent, inactive, or a
 * separator. A generated code is none of those — it is valid, unique, and works
 * everywhere the customer's own code works. Dimming it would tell the admin their
 * data is second-class when the only fact is that they did not type it.
 *
 * WHY THE MARKER IS TEXT AND NOT AN ICON
 * --------------------------------------
 * svdy-ui §14: an icon is a claim. There is no glyph for "we generated this" that
 * does not also suggest a robot, a warning, or a sync — three claims this product
 * does not want to make about a perfectly good code. A word is unambiguous in nine
 * languages; a glyph is not.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@mui/material';

interface Props {
  code?: string | null;
  /** `'customer' | 'generated' | 'external'`. Only `'generated'` is marked. */
  codeSource?: string | null;
}

export const CodeCell: React.FC<Props> = ({ code, codeSource }) => {
  const { t } = useTranslation();

  if (!code) {
    // Unreachable for departments and positions since ADR-043 S4 made the column
    // NOT NULL, but branches and employment types still allow an empty code — and
    // an em dash is what the rest of this app renders for absent (svdy-ui §7).
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        —
      </Box>
    );
  }

  if (codeSource !== 'generated') {
    return <>{code}</>;
  }

  return (
    <Tooltip title={t('org:shared.codeField.generatedTooltip')} placement="top">
      <Box component="span" sx={{ cursor: 'help', whiteSpace: 'nowrap' }}>
        {code}
        <Box
          component="span"
          sx={{
            color: 'text.disabled',
            ml: 0.75,
            fontSize: '0.7rem',
            fontWeight: 400,
          }}
        >
          · {t('org:shared.codeField.generatedMarker')}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default CodeCell;
